import { useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useProductCategories } from '@/hooks/warehouse/useProductCategories';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import ProductCategoryFormModal from './ProductCategoryFormModal';

interface ProductCategoryManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProductCategoryManagerModal({ open, onOpenChange }: ProductCategoryManagerModalProps) {
  const [editingCategory, setEditingCategory] = useState<{ id: string; name: string } | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const {
    categories,
    deleteCategory,
    reorderCategory,
    isDeleting,
    isReordering,
  } = useProductCategories();

  const handleCreate = () => {
    setEditingCategory(null);
    setIsFormOpen(true);
  };

  const handleEdit = (category: { id: string; name: string }) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory(id);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleMoveUp = async (currentIndex: number) => {
    if (currentIndex === 0 || isReordering) return;

    const current = categories[currentIndex];
    const target = categories[currentIndex - 1];

    await reorderCategory({
      categoryId: current.id,
      newSortOrder: target.sort_order,
      swapCategoryId: target.id,
      swapSortOrder: current.sort_order,
    });
  };

  const handleMoveDown = async (currentIndex: number) => {
    if (currentIndex === categories.length - 1 || isReordering) return;

    const current = categories[currentIndex];
    const target = categories[currentIndex + 1];

    await reorderCategory({
      categoryId: current.id,
      newSortOrder: target.sort_order,
      swapCategoryId: target.id,
      swapSortOrder: current.sort_order,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Gestionar Categorías</DialogTitle>
            <DialogDescription>
              Administra las categorías de productos. Puedes crear, editar, eliminar y reordenar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={handleCreate} size="sm" className="bg-brand-primary hover:bg-brand-primary-hover">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Categoría
              </Button>
            </div>

            {/* Categories list */}
            <div className="border rounded-lg divide-y max-h-[60vh] overflow-y-auto">
              {categories.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No hay categorías configuradas.
                </div>
              ) : (
                categories.map((category, index) => (
                  <div
                    key={category.id}
                    className="flex items-center gap-2 p-3 hover:bg-accent/50 group"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground" />
                    <span className="flex-1 font-medium">{category.name}</span>
                    <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === categories.length - 1 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(category)}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
                            <AlertDialogDescription>
                              La categoría "{category.name}" se eliminará. Los productos
                              asociados deberán ser reasignados a otra categoría.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(category.id)}
                              disabled={isDeleting}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {isDeleting ? 'Eliminando...' : 'Eliminar'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProductCategoryFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        categoryId={editingCategory?.id}
        initialName={editingCategory?.name}
      />
    </>
  );
}
